export function shortWallet(pubkey: string | null | undefined, n = 8): string {
  if (!pubkey) {
    return "";
  }
  return pubkey.slice(0, n) + " ... " + pubkey.slice(-n);
}

export const getCountryName = (countryCode: string) => {
  const { locale } = useI18n();
  const displayNames = new Intl.DisplayNames([locale.value], {
    type: "region",
  });
  return displayNames.of(countryCode);
};
export const getCurrentFullPath = (clientPath: string = "") => {
  const route = useRoute();
  const path = clientPath || route.fullPath;
  const url = new URL(path, "http://localhost");
  url.searchParams.delete("cause");
  return `${url.pathname}${url.search}${url.hash}`;
};

export const constructHeaders = (
  authToken?: string,
  additionalHeaders?: { [key: string]: string }
): { [key: string]: string } => {
  const headers: { [key: string]: string } = {};

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  return { ...headers, ...additionalHeaders };
};

export const logWarning = () => {
  const { debugMode } = useRuntimeConfig().public;
  if (debugMode) return;
  console.log(
    "%cHello! Stay awhile and listen",
    "color: green; font-weight: bold; font-size: 40px; "
  );

  console.log(
    "%cInserting code here could compromise your account security. If anyone asked you to do something here, it's highly likely you're being scammed. If you're having any issues, please send us a ticket using the support widget in the bottom right corner of the screen.",
    "color: red; font-weight: bold; font-size: 20px;"
  );
};

export const isMobileDevice = (): boolean => {
  if (typeof navigator === "undefined") {
    return false;
  }
  return /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
    navigator.userAgent.toLowerCase()
  );
};

export const refreshURL = async (url: string) => {
  const timestamp = new Date().getTime();
  const separator = url.includes("?") ? "&" : "?";
  const newUrl = url + separator + "timestamp=" + timestamp;
  try {
    await fetch(newUrl, { cache: "reload" }).then((response) => {
      response.text();
    });
  } catch (error) {
    console.warn(error);
  }
};

export function parseJwt(token: string) {
  if (!token) return null;
  var base64Url = token.split(".")[1];
  if (!base64Url) return null;
  var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const decodeBase64 =
    typeof atob !== "undefined"
      ? (value: string) => atob(value)
      : (value: string) => Buffer.from(value, "base64").toString("binary");
  var jsonPayload = decodeURIComponent(
    decodeBase64(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
}

export const generateAndStoreDeviceId = () => {
  const id = useCookie("deviceId", {
    maxAge: 7776000,
    expires: new Date(Date.now() + 7776000000),
    sameSite: "strict",
    secure: true,
  });

  if (!id.value) id.value = generateUUID();
};

export const storeReferralId = () => {
  const route = useRoute();
  if (route.query.referral) {
    const referralId = useCookie("referral", {
      maxAge: 604800,
      expires: new Date(Date.now() + 604800000),
      sameSite: "strict",
      secure: true,
    });

    referralId.value = route.query.referral as string;
  }
};

/**
 * Adds query parameters to a URL, handling existing parameters correctly
 * @param url - The base URL (can include existing query parameters)
 * @param params - Object containing query parameters to add
 * @returns The URL with properly formatted query parameters
 */
export const addQueryParams = (
  url: string,
  params: Record<string, string | number | boolean | null | undefined>
): string => {
  try {
    const urlObj = new URL(url, window.location.origin);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        urlObj.searchParams.set(key, String(value));
      }
    });

    return urlObj.toString();
  } catch (error) {
    const separator = url.includes("?") ? "&" : "?";
    const paramString = Object.entries(params)
      .filter(([_, value]) => value !== null && value !== undefined)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
      )
      .join("&");

    return paramString ? `${url}${separator}${paramString}` : url;
  }
};

/**
 * Removes specific query parameters from a URL
 * @param url - The URL to modify
 * @param paramNames - Array of parameter names to remove
 * @returns The URL with specified parameters removed
 */
export const removeQueryParams = (
  url: string,
  paramNames: string[]
): string => {
  try {
    const urlObj = new URL(url, window.location.origin);

    paramNames.forEach((param) => {
      urlObj.searchParams.delete(param);
    });

    return urlObj.toString();
  } catch (error) {
    // Fallback for invalid URLs
    return url;
  }
};
